import React, { useState } from "react";
import {
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Label,
  Row,
  Button,
  Form,
  FormFeedback,
  Alert,
  Spinner,
} from "reactstrap";
import { Link } from "react-router-dom";
import * as Yup from "yup";
import { useFormik } from "formik";
import { postForgotPassword } from "../../helpers/eirna_backend_helper";
import eirnaLogo from "../../assets/images/eirna/eirnabio-connect-logo.png";
import LoginCarousel from "./LoginCarousel";

const ForgetPasswordPage = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      email: "",
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .email("Please enter a valid email")
        .required("Please enter your email"),
    }),
    onSubmit: async (values) => {
      setLoading(true);
      setError("");

      try {
        const response = await postForgotPassword({
          email: values.email,
        });

        if (response) {
          setSuccess(true);
        }
      } catch (err) {
        const errorMessage =
          err?.response?.data?.message ||
          err?.message ||
          "Failed to send reset email";
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    },
  });

  document.title = "Reset Password | EIRNABio Connect";

  return (
    <React.Fragment>
      <div
        style={{
          width: "100vw",
          minHeight: "100vh",
          margin: 0,
          padding: 0,
          overflow: "auto",
        }}
      >
        <Row className="g-0" style={{ minHeight: "100vh", margin: 0 }}>
          {/* Left Panel - Carousel Section */}
          <Col
            xl={6}
            lg={6}
            md={6}
            style={{
              padding: "0",
              position: "relative",
              minHeight: "100vh",
            }}
          >
            <div
              style={{
                width: "100%",
                position: "relative",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <LoginCarousel />
            </div>
          </Col>

          {/* Right Panel - Forgot Password Form Section */}
          <Col
            xl={6}
            lg={6}
            md={6}
            style={{
              backgroundColor: "white",
              minHeight: "100vh",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "0",
            }}
          >
            <div
              style={{
                width: "100%",
                maxWidth: "500px",
                padding: "40px",
              }}
            >
              {/* EIRNABio Logo Section */}
              <div className="text-center mb-4">
                <Link to="/" className="d-inline-block auth-logo">
                  <img
                    src={eirnaLogo}
                    alt="EIRNABio Connect"
                    height="80"
                    style={{ marginBottom: "20px" }}
                  />
                </Link>
              </div>

              {/* Welcome Text */}
              <div className="text-center mb-4">
                <h5
                  style={{
                    fontWeight: "600",
                    fontSize: "24px",
                    color: "#333",
                    marginBottom: "8px",
                  }}
                >
                  Forgot Password?
                </h5>
                <p
                  style={{
                    color: "#666",
                    fontSize: "14px",
                    margin: "0",
                  }}
                >
                  Enter your email and we'll send you a reset link
                </p>
              </div>

              {/* Success Alert */}
              {success && (
                <Alert color="success" className="mb-4">
                  <i className="mdi mdi-check-circle me-2"></i>
                  An email has been sent with password reset instructions.
                  Please check your inbox and spam folder.
                </Alert>
              )}

              {/* Error Alert */}
              {error && error ? (
                <Alert color="danger" className="mb-4">
                  <i className="mdi mdi-block-helper me-2"></i>
                  {error}
                </Alert>
              ) : null}

              <Form
                onSubmit={(e) => {
                  e.preventDefault();
                  validation.handleSubmit();
                  return false;
                }}
              >
                {/* Email Field */}
                <div className="mb-3">
                  <Label
                    htmlFor="email"
                    style={{
                      fontWeight: "500",
                      color: "#333",
                      marginBottom: "8px",
                      display: "block",
                    }}
                  >
                    Email
                  </Label>
                  <Input
                    name="email"
                    placeholder="Enter your email"
                    type="email"
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    value={validation.values.email || ""}
                    invalid={
                      validation.touched.email && validation.errors.email
                        ? true
                        : false
                    }
                    style={{
                      padding: "12px 16px",
                      border: "1px solid #e0e0e0",
                      borderRadius: "8px",
                      fontSize: "14px",
                    }}
                  />
                  {validation.touched.email && validation.errors.email ? (
                    <FormFeedback type="invalid">
                      {validation.errors.email}
                    </FormFeedback>
                  ) : null}
                </div>

                {/* Submit Button */}
                <Button
                  color="success"
                  disabled={loading}
                  className="w-100"
                  type="submit"
                  style={{
                    backgroundColor: "#0A5F3E",
                    borderColor: "#0A5F3E",
                    padding: "12px",
                    fontSize: "16px",
                    fontWeight: "500",
                    borderRadius: "8px",
                    border: "none",
                    marginTop: "16px",
                  }}
                >
                  {loading ? (
                    <>
                      <Spinner size="sm" className="me-2">
                        Loading...
                      </Spinner>
                      Sending Reset Link...
                    </>
                  ) : (
                    "Send Reset Link"
                  )}
                </Button>
              </Form>

              {/* Back to Login Section */}
              <div className="text-center mt-4">
                <p style={{ fontSize: "14px", color: "#666", margin: "0" }}>
                  Remember your password?{" "}
                  <Link
                    to="/login"
                    style={{
                      color: "#0A5F3E",
                      fontWeight: "500",
                      textDecoration: "none",
                    }}
                  >
                    Back to Login
                  </Link>
                </p>
              </div>
            </div>
          </Col>
        </Row>
      </div>
    </React.Fragment>
  );
};

export default ForgetPasswordPage;
